import React, { useEffect, useState } from 'react';

import { filter, includes, has, sortBy } from 'lodash';

import { object, array } from 'prop-types';

import { connect } from 'react-redux';

import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';

import ActionBar from '../../components/ActionBar';
import ActionBarItem from '../../components/ActionBarItem';
import { TablePagination } from '@material-ui/core';
import MaterialTable from 'material-table';

import TechnicianTableRowMenu from './TechnicianTableRowMenu';

import * as ArchitectActions from '../../actions/architectActions';
import * as ClientActions from '../../actions/clientActions';
import * as CoreActions from '../../actions/coreActions';
import * as ServiceManagementActions from '../../actions/serviceManagementActions';
import * as SiteActions from '../../actions/siteActions';

import classes from './TechniciansList.module.css';

const TechniciansList = ({
    openCreatePanel,
    selectedTechnician,
    setSelectedTechnician,
    fetchTechniciansList,
    order,
    orderBy,
    handleSort,
    techniciansList,
    closeCreatePanel,
    techniciansLoading,
    onToggleCreateTechnicianModal,
    ...props
}) => {
    const [technicians, setTechnicians] = useState([]);
    const [page, setPage] = useState(0);
    const [searchFilter, setSearchFilter] = useState();
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [activeFilter, setActiveFilter] = useState(true);

    useEffect(() => {
        if (techniciansList[0] && has(techniciansList[0], 'items')) {
            setTechnicians(techniciansList[0].items);
            if (searchFilter) {
                handleSearch(searchFilter);
            }
        }
    }, [techniciansList]);

    const handleSearch = searchFilter => {
        setSearchFilter(searchFilter);

        if (searchFilter.length > 0) {
            const filteredTechnicians = filter(techniciansList[0].items, technician => includes(technician.name.toLowerCase(), searchFilter.toLowerCase()));
            setTechnicians(filteredTechnicians);
        } else {
            setTechnicians(techniciansList[0].items);
        }

    };

    const handleClick = (event, rowData) => {
        closeCreatePanel();
        setSelectedTechnician(rowData);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getTechniciansForPage = () => {
        const startIndex = page * rowsPerPage;
        const endIndex = (page + 1) * rowsPerPage;

        const sortedTechnicians = sortBy(technicians, technician => technician.name.toUpperCase());

        if (activeFilter) {
            return sortedTechnicians.filter(technician => technician.isActive === true).slice(startIndex, endIndex);
        }

        return sortedTechnicians.slice(startIndex, endIndex);
    };

    const openDeleteTechnicianModal = () => {
        onToggleCreateTechnicianModal(true);
    };

    const renderMenu = (technician) => {
        return (
            <TechnicianTableRowMenu
                technician={technician}
                openDeleteTechnicianModal={() => openDeleteTechnicianModal()}
            />
        );
    };

    const renderActiveStatus = (technician) => {
        if (technician.isActive) {
            return 'Active';
        }

        return 'Inactive';
    };

    const nameSorter = (a, b) => {
        const valueA = a.name.toUpperCase();
        const valueB = b.name.toUpperCase();

        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }

        return 0;
    };

    const toggleActiveFilter = () => {
        setActiveFilter(!activeFilter);
    };

    // Define columns prior to injecting them into component's state
    const columns = [
        {
            title:      'Name',
            field:      'name',
            customSort: nameSorter
        },
        {
            title:  'Status',
            field:  'isActive',
            render: rowData => renderActiveStatus(rowData)
        },
        {
            title:  '',
            render: rowData => renderMenu(rowData)
        }
    ];

    // This is a WORK AROUND for a Material Table (as of v1.68) issue where column sort state was lost when parent
    // component's state updates. This technique is not meant for global adoption; check on the current
    // state of Material Table & determine if a solution has been introduced before copy/pasting this code.
    // That said, lifting the columns into the parent's state preserves it and prevents state change from
    // "dropping" the current column sort order.
    //
    // https://github.com/mbrn/material-table/issues/1265
    const [tableColumns] = useState(columns);

    return (
        <Paper className={classes.listContainer}>
            <ActionBar
                onSearch={handleSearch}
                customClass={classes.technicianSearch}
            >
                <ActionBarItem
                    onClick={openCreatePanel}
                    type="add"
                >
                    New Technician
                </ActionBarItem>
                {activeFilter ?
                    <ActionBarItem
                        onClick={toggleActiveFilter}
                        type="include"
                    >
                        Show Inactive
                    </ActionBarItem>
                    :
                    <ActionBarItem
                        onClick={toggleActiveFilter}
                        type="exclude"
                    >
                        Hide Inactive
                    </ActionBarItem>
                }

            </ActionBar>
            {techniciansLoading ?
                <div className={classes.loader}>
                    <CircularProgress size={50} />
                </div>
                :
                <React.Fragment>
                    <div className={classes.table}>
                        <MaterialTable
                            options={{
                                paging:              true,
                                showTitle:           false,
                                search:              false,
                                selection:           true,
                                toolbar:             false,
                                maxBodyHeight:       '850px',
                                rowStyle:            rowData => ({ backgroundColor: rowData === selectedTechnician ? '#EEE' : '#FFF' }),
                                emptyRowsWhenPaging: false,
                                pageSize:            rowsPerPage
                            }}
                            columns={tableColumns}
                            data={getTechniciansForPage()}
                            onRowClick={(event, rowData) => handleClick(event, rowData)}
                            components={{
                                Pagination: props => {
                                    return (
                                        <TablePagination
                                            {...props}
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                            count={technicians.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onChangePage={handleChangePage}
                                            onChangeRowsPerPage={handleChangeRowsPerPage}
                                        />
                                    );
                                }
                            }}
                        />
                    </div>
                </React.Fragment>
            }
        </Paper>
    );
};

TechniciansList.defaultProps = {
    companyInfo:     {},
    techniciansList: [
        {
            items:     [],
            name:      '',
            itemProps: null
        }
    ]
};

TechniciansList.propTypes = {
    companyInfo:     object,
    techniciansList: array
};

const mapStateToProps = state => {
    return {
        companyInfo:        state.companyInfo,
        selectedTechnician: state.architect.selectedTechnician,
        page:               state.architect.page,
        rowsPerPage:        state.architect.rowsPerPage,
        techniciansLoading: state.architect.techniciansLoading
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ClientActions,
    ...CoreActions,
    ...ServiceManagementActions,
    ...SiteActions
};

export default connect(mapStateToProps, mapDispatchToProps)(TechniciansList);
